import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { X, UserPlus, MagnifyingGlass, Check } from "@phosphor-icons/react";
import api from "../lib/api";
import { normalizeHandle } from "../lib/format";

/**
 * Rich customer @ picker with:
 * - Avatar (customer photo or colored initial)
 * - @arroba (bold) + full name (subtle) rows
 * - Substring search over @ and name
 * - Quick-create when no match, opens a mini modal (name + @)
 *
 * Props:
 *   value: string – current @ value shown in the input
 *   onChange: (handleStr) => void – called on every keystroke or selection
 *   onSelectCustomer?: (customerObj) => void – called when a customer is picked (has id)
 *   customers: array – customer list
 *   onCustomerCreated: () => void – called after quick-create so parent can refetch
 *   testid?: string – base for data-testids
 *   placeholder?: string
 *   inputRef?: React ref – forwarded to the underlying input
 */
export default function CustomerHandlePicker({
  value, onChange, onSelectCustomer, customers = [], onCustomerCreated,
  testid = "handle", placeholder = "@cliente", inputRef, autoFocus,
}) {
  const [open, setOpen] = useState(false);
  const [showQuick, setShowQuick] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef(null);
  const localInputRef = useRef(null);
  const usedInputRef = inputRef || localInputRef;

  const query = (value || "").trim().toLowerCase().replace(/^@/, "");
  const matches = useMemo(() => {
    if (!customers?.length) return [];
    if (!query) return customers.slice(0, 30);
    return customers
      .filter(c => {
        const h = (c.handle || "").toLowerCase().replace(/^@/, "");
        const n = (c.full_name || "").toLowerCase();
        return h.includes(query) || n.includes(query);
      })
      .slice(0, 30);
  }, [customers, query]);

  const exactMatch = useMemo(() => {
    if (!query) return null;
    const norm = normalizeHandle(query);
    return customers.find(c => (c.handle || "").toLowerCase() === norm);
  }, [customers, query]);

  useEffect(() => {
    if (highlight >= matches.length) setHighlight(0);
  }, [matches.length, highlight]);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const select = (c) => {
    onChange(c.handle);
    onSelectCustomer?.(c);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      setOpen(true); return;
    }
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight(h => Math.min(h + 1, matches.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight(h => Math.max(h - 1, 0));
    } else if (e.key === "Enter" && matches[highlight]) {
      e.preventDefault();
      select(matches[highlight]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapRef} className="relative">
      <div className="flex gap-1">
        <input
          ref={usedInputRef}
          data-testid={`${testid}-input`}
          className="input-brutal flex-1 min-w-0"
          placeholder={placeholder}
          value={value}
          autoComplete="off"
          autoCapitalize="off"
          spellCheck={false}
          autoFocus={autoFocus}
          onFocus={() => setOpen(true)}
          onChange={(e) => { onChange(e.target.value); setOpen(true); }}
          onKeyDown={onKeyDown}
        />
        {query && !exactMatch && (
          <button
            type="button"
            data-testid={`${testid}-quick-add`}
            title="Cadastrar novo cliente com esta @"
            onClick={() => setShowQuick(true)}
            className="btn-yellow !px-3 shrink-0"
          >
            <UserPlus weight="bold" size={16} />
          </button>
        )}
      </div>

      {open && matches.length > 0 && (
        <div
          data-testid={`${testid}-dropdown`}
          className="absolute z-40 left-0 right-0 mt-1 max-h-72 overflow-y-auto card-brutal bg-white p-1"
        >
          <ul className="divide-y divide-brand-ink/10">
            {matches.map((c, i) => (
              <li key={c.id}>
                <button
                  type="button"
                  data-testid={`${testid}-option-${(c.handle||'').replace(/^@/,'')}`}
                  onMouseEnter={() => setHighlight(i)}
                  onClick={() => select(c)}
                  className={`w-full text-left px-2 py-2 flex items-center gap-2 ${
                    i === highlight ? "bg-brand-yellow" : "hover:bg-brand-cream"
                  }`}
                >
                  <Avatar name={c.full_name} handle={c.handle} photoUrl={c.photo_url} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold truncate">{c.handle}</p>
                    <p className="text-xs text-brand-ink2 truncate">{c.full_name || "—"}</p>
                  </div>
                  {exactMatch?.id === c.id && (
                    <Check weight="bold" size={14} className="text-brand-success" />
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {showQuick && (
        <QuickCreateModal
          initialHandle={normalizeHandle(query) || "@"}
          onClose={() => setShowQuick(false)}
          onCreated={(cust) => {
            setShowQuick(false);
            onChange(cust.handle);
            onSelectCustomer?.(cust);
            onCustomerCreated?.();
            setOpen(false);
          }}
        />
      )}
    </div>
  );
}

function Avatar({ name, handle, photoUrl }) {
  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt={name || handle || "avatar"}
        className="w-8 h-8 shrink-0 border-2 border-brand-ink rounded-full object-cover"
      />
    );
  }
  const seed = (name || handle || "?").trim();
  const initial = seed[0]?.toUpperCase() || "?";
  // Deterministic bg color pick from a small palette
  const palette = ["bg-brand-pink", "bg-brand-mint", "bg-brand-yellow", "bg-brand-peach", "bg-brand-purple"];
  let hash = 0;
  for (const ch of seed) hash = (hash * 31 + ch.charCodeAt(0)) >>> 0;
  const bg = palette[hash % palette.length];
  const isYellow = bg === "bg-brand-yellow" || bg === "bg-brand-peach";
  return (
    <span className={`w-8 h-8 shrink-0 border-2 border-brand-ink rounded-full flex items-center justify-center font-heading font-black text-sm ${bg} ${isYellow ? "text-brand-ink" : "text-white"}`}>
      {initial}
    </span>
  );
}

function QuickCreateModal({ initialHandle, onClose, onCreated }) {
  const [handle, setHandle] = useState(initialHandle);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    const h = normalizeHandle(handle);
    const body = h.replace(/^@/, "");
    if (!body || !/^[a-z0-9._]{1,30}$/.test(body)) {
      return toast.error("@arroba inválido — letras, números, '.' ou '_' (até 30)");
    }
    if (!name.trim()) return toast.error("Informe o nome completo");
    setSaving(true);
    try {
      const { data } = await api.post("/customers", { handle: h, full_name: name.trim() });
      toast.success("Cliente cadastrado");
      onCreated(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao cadastrar");
    } finally { setSaving(false); }
  };

  return (
    <div
      data-testid="quick-create-modal"
      className="fixed inset-0 bg-brand-ink/60 z-[80] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="card-brutal bg-white w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between mb-3">
          <div>
            <p className="label-brutal">Novo cliente</p>
            <h3 className="font-heading text-xl font-black">Cadastro rápido</h3>
          </div>
          <button
            type="button"
            data-testid="quick-create-close"
            onClick={onClose}
            className="p-2 border-2 border-brand-ink hover:bg-brand-peach"
          >
            <X weight="bold" size={14} />
          </button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="label-brutal">Nome completo *</label>
            <input
              data-testid="quick-create-name"
              className="input-brutal"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Maria Silva"
              autoFocus
              required
            />
          </div>
          <div>
            <label className="label-brutal">@arroba *</label>
            <input
              data-testid="quick-create-handle"
              className="input-brutal"
              value={handle}
              onChange={(e) => setHandle(e.target.value)}
              placeholder="@cliente"
              autoCapitalize="off"
              spellCheck={false}
              required
            />
            <p className="text-[11px] text-brand-ink2 mt-1">Use letras, números, '.' ou '_' (até 30 chars).</p>
          </div>
          <p className="text-xs text-brand-ink2">
            Você pode completar endereço, medidas e preferências mais tarde em <strong>Clientes</strong>.
          </p>
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="btn-white">Cancelar</button>
            <button
              type="submit"
              data-testid="quick-create-submit"
              disabled={saving}
              className="btn-pink"
            >
              {saving ? "Salvando…" : "Cadastrar cliente"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
